import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth, checkIsAdmin, checkIsApproved, checkUserProfile } from '../firebase';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  isAdmin: boolean;
  isApproved: boolean;
  isProfileComplete: boolean;
  refreshUserStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  isAdmin: false,
  isApproved: false,
  isProfileComplete: false,
  refreshUserStatus: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [isProfileComplete, setIsProfileComplete] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  const checkUserStatus = async (user: User | null) => {
    if (user) {
      console.log("Checking user status for", user.uid);
      console.log("User data", user);
      const [adminStatus, approvedStatus, profileStatus] = await Promise.all([
        checkIsAdmin(user.uid),
        checkIsApproved(user.uid),
        checkUserProfile(user.uid)
      ]);
      setIsAdmin(adminStatus);
      setIsApproved(approvedStatus);
      setIsProfileComplete(profileStatus);
    } else {
      setIsAdmin(false);
      setIsApproved(false);
      setIsProfileComplete(false);
    }
  };

  const refreshUserStatus = async () => {
    if (currentUser) {
      await checkUserStatus(currentUser);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await checkUserStatus(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    isAdmin,
    isApproved,
    isProfileComplete,
    refreshUserStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 